import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import PtfButton from "@/components/PtfButton";
import { Reveal, MaskText } from "@/components/motion/Reveal";
import { PATHWAYS, FLAGSHIP_PATHWAY } from "@/lib/learning";

const Eyebrow = ({ children, light }) => (
  <p className={`text-[0.7rem] uppercase tracking-[0.28em] mb-5 flex items-center gap-3 ${light ? "text-ptf-accent" : "text-ptf-primary/60"}`}>
    <span className={`h-px w-8 inline-block ${light ? "bg-ptf-accent" : "bg-ptf-primary/40"}`} />{children}
  </p>
);

export default function Pathways() {
  return (
    <>
      <Seo title="Learning Pathways" path="/learning/pathways" description="Six recommended Planet Trotter learning pathways plus the signature Field-Ready Adventurer route: from first module to working field competence in conservation, tourism, adventure, community, agriculture and storytelling." />
      <PageHero
        eyebrow="Field Learning · Pathways"
        titleLines={["One module at", "a time."]}
        intro="Pathways are recommended sequences through the catalogue. Follow one end to end, or take what you need. Programme availability follows each programme's own status."
        image="/dawn/vor-hillside.jpg"
      />

      <section data-testid="flagship-pathway" className="bg-ptf-primary text-ptf-bg py-16 md:py-24 grain">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
          <Reveal><Eyebrow light>Signature pathway</Eyebrow></Reveal>
          <MaskText
            lines={["Field-Ready", "Adventurer."]}
            className="font-display font-extrabold uppercase text-4xl sm:text-6xl leading-[0.92] tracking-tight"
          />
          <Reveal delay={0.15}>
            <p className="mt-5 text-ptf-bg/75 leading-relaxed max-w-2xl">{FLAGSHIP_PATHWAY.blurb}</p>
          </Reveal>
          <div className="mt-10 flex flex-wrap items-center gap-2" data-testid="flagship-steps">
            {FLAGSHIP_PATHWAY.steps.map((s, i) => (
              <Reveal key={s} delay={i * 0.04} className="flex items-center gap-2">
                <span className="border border-ptf-bg/25 px-3.5 py-2 text-[0.68rem] uppercase tracking-[0.12em] text-ptf-bg/85">{s}</span>
                {i < FLAGSHIP_PATHWAY.steps.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-ptf-accent" />}
              </Reveal>
            ))}
          </div>
          <div className="mt-10">
            <PtfButton to="/learning/training?category=adventure-outdoor" variant="accent" data-testid="flagship-cta">Start With Outdoor Skills</PtfButton>
          </div>
        </div>
      </section>

      <section className="bg-ptf-bg py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
          <Reveal><Eyebrow>Six recommended pathways</Eyebrow></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {PATHWAYS.map((pw, i) => (
              <Reveal key={pw.num} delay={(i % 2) * 0.06}>
                <div data-testid={`pathway-${pw.num}`} className="bg-white border border-ptf-primary/12 p-7 h-full">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-display text-3xl text-ptf-primary/25">{pw.num}</span>
                  </div>
                  <h3 className="mt-1 font-display uppercase text-2xl tracking-tight text-ptf-primary">{pw.name}</h3>
                  <p className="mt-2 text-sm text-ptf-text/65 leading-relaxed">{pw.blurb}</p>
                  <div className="mt-5 flex flex-wrap items-center gap-1.5">
                    {pw.steps.map((s, j) => (
                      <span key={s} className="flex items-center gap-1.5">
                        <span className="bg-ptf-primary/5 text-ptf-primary px-2.5 py-1.5 text-[0.62rem] uppercase tracking-[0.1em]">{s}</span>
                        {j < pw.steps.length - 1 && <ArrowRight className="h-3 w-3 text-ptf-primary/30" />}
                      </span>
                    ))}
                  </div>
                  <Link to="/learning/training" className="mt-5 inline-block text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-ptf-secondary hover:text-ptf-primary transition-colors">
                    Find these programmes in the catalogue
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
